import React, { ReactNode } from 'react'
import styled from 'styled-components'
import Tooltip from 'components/Tooltip'
import { Flex } from 'components/Box'
import Badge from 'modules/shares/components/AvatarContainer'
import { memberList } from 'styles/theme/variants/memberList'
interface OverflowBadgeProps {
  size: number
  rightMargin: number
}

interface OverflowBadgeProps {
  width?: string | number
  height?: string | number
  'margin-right'?: string | number
  children: ReactNode
}

export const OverflowBadge = styled(Badge)`
  cursor: pointer;
  width: ${(props: OverflowBadgeProps) => props.size}px;
  height: ${(props: OverflowBadgeProps) => props.size}px;
  margin-right: ${(props: OverflowBadgeProps) => props.rightMargin}px;
`

const AvatarContainer = styled.div`
  margin-right: ${(props: { margin: number }) => props.margin}px;
`

function getNumberOfAvatarsToRender(
  avatarMargin: number,
  { maxAvatars = 9, maxWidth, avatarSize, totalCount }: AvatarStackProps
) {
  const maxItems =
    typeof maxWidth === 'number' && !isNaN(maxWidth)
      ? Math.floor(maxWidth / (avatarSize + avatarMargin))
      : maxAvatars

  const numberOfAvatarsToRender =
    totalCount > maxItems ? maxItems - 1 : maxItems

  const overflowCount = totalCount - numberOfAvatarsToRender

  return { numberOfAvatarsToRender, overflowCount }
}

interface AvatarStackProps {
  /** Avatar size in px */
  avatarSize: number
  /** Custom avatar margin-right in px */
  avatarMargin?: number
  /** Max number of avatars to render */
  maxAvatars?: number
  /** Max space to render avatars within */
  maxWidth?: number
  /** Total number of avatars */
  totalCount: number
  /** What to display in tooltip on the overflow badge, `$OVERFLOW` will be replaced with the number of avatars left */
  tooltipContent?: string
  /** Boolean to enable/disable Badge Overflow tooltip */
  tooltipDisabled?: boolean
  /** Boolean to show total avatars count or just overflow number in overflow badge */
  showTotalCountInBadge?: boolean
}

const AvatarStack: React.FunctionComponent<AvatarStackProps> = ({
  tooltipDisabled = false,
  tooltipContent = '$OVERFLOW more users',
  avatarMargin = memberList.avatarMargin,
  showTotalCountInBadge = false,
  ...props
}) => {
  const { numberOfAvatarsToRender, overflowCount } = getNumberOfAvatarsToRender(
    avatarMargin,
    props
  )

  return (
    <Flex {...props}>
      {React.Children.map(props.children, child => (
        <AvatarContainer margin={avatarMargin}>{child}</AvatarContainer>
      ))?.slice(0, numberOfAvatarsToRender)}
      {overflowCount > 1 && (
        <Tooltip
          placement="bottom"
          spacing="14px"
          disabled={tooltipDisabled}
          content={
            <span>
              {tooltipContent.replace('$OVERFLOW', String(overflowCount))}
            </span>
          }>
          {tooltipProps => (
            <OverflowBadge
              size={props.avatarSize}
              rightMargin={avatarMargin}
              data-testid="overflow-badge"
              {...tooltipProps}>
              {showTotalCountInBadge ? props.totalCount : overflowCount}
            </OverflowBadge>
          )}
        </Tooltip>
      )}
    </Flex>
  )
}

export default AvatarStack
